const normalizeID = (value) => (
  value === undefined || value === null ? "" : `${value}`
);

const getSectionTermValues = (sectionInfo = {}) => Array.from(new Set(
  (Array.isArray(sectionInfo.quarter_days) ? sectionInfo.quarter_days : [])
    .map((quarterDay) => normalizeID(quarterDay?.quarter))
    .filter(Boolean)
));

const getSectionStudentIDs = (sectionInfo = {}, sectionsByID = {}) => {
  const sectionParts = [
    sectionInfo,
    ...(Array.isArray(sectionInfo.subsections) ? sectionInfo.subsections : [])
      .map((subsectionID) => sectionsByID[normalizeID(subsectionID)])
      .filter(Boolean),
  ];

  return sectionParts.reduce((studentIDs, sectionPart) => {
    (Array.isArray(sectionPart.student_list) ? sectionPart.student_list : [])
      .forEach((studentID) => {
        const normalizedStudentID = normalizeID(studentID);
        if (normalizedStudentID) studentIDs.add(normalizedStudentID);
      });
    return studentIDs;
  }, new Set());
};

/**
 * Returns the highest number of unique students occupying a classroom-period
 * cell in any one term. Sequential quarter/semester sections are not summed.
 */
export const getClassroomPeriodStudentCount = ({
  displaySections = [],
  allSections = [],
  subsectionParentByID = {},
} = {}) => {
  const sectionsByID = (Array.isArray(allSections) ? allSections : []).reduce(
    (lookup, sectionInfo) => {
      const sectionID = normalizeID(sectionInfo?.section_id);
      if (sectionID) lookup[sectionID] = sectionInfo;
      return lookup;
    },
    {}
  );
  const loggedMainSectionIDs = new Set();
  const occupancyRecords = [];
  const knownTerms = new Set();

  (Array.isArray(displaySections) ? displaySections : []).forEach((displaySection) => {
    const displaySectionID = normalizeID(displaySection?.section_id);
    if (!displaySectionID) return;

    const mainSectionID = normalizeID(subsectionParentByID[displaySectionID]) || displaySectionID;
    if (loggedMainSectionIDs.has(mainSectionID)) return;
    loggedMainSectionIDs.add(mainSectionID);

    const mainSection = sectionsByID[mainSectionID] || displaySection;
    const terms = getSectionTermValues(mainSection);
    terms.forEach((term) => knownTerms.add(term));
    occupancyRecords.push({
      studentIDs: getSectionStudentIDs(mainSection, sectionsByID),
      terms,
    });
  });

  if (occupancyRecords.length === 0) return 0;

  const fallbackTerms = knownTerms.size > 0 ? Array.from(knownTerms) : ["all"];
  const studentsByTerm = {};

  occupancyRecords.forEach(({ studentIDs, terms }) => {
    const occupiedTerms = terms.length > 0 ? terms : fallbackTerms;

    occupiedTerms.forEach((term) => {
      if (!studentsByTerm[term]) studentsByTerm[term] = new Set();
      studentIDs.forEach((studentID) => studentsByTerm[term].add(studentID));
    });
  });

  return Object.values(studentsByTerm).reduce(
    (highestCount, studentIDs) => Math.max(highestCount, studentIDs.size),
    0
  );
};

